/**
 * Villamine taxonomy, verified against villamine.com on 2026-09-29.
 * See docs/01-DATA-SOURCES.md and docs/03-FILTER-TAXONOMY.md.
 */
import type { BooleanAttribute, SiteLocale } from "./villa";

export const VILLAMINE_ORIGIN = "https://villamine.com";
export const VILLAMINE_IMAGE_BASE = "https://www.villamine.com/img/villa/";

/** Search regions ("bölge") and their ids from the site's region select. */
export const REGIONS: { id: string; name: string; aliases: string[] }[] = [
  { id: "4006", name: "Kalkan", aliases: ["kalkan"] },
  { id: "4009", name: "Kaş", aliases: ["kas", "kash"] },
  { id: "4010", name: "Fethiye", aliases: ["fethiye"] },
  { id: "5034", name: "Bodrum", aliases: ["bodrum"] },
  { id: "A0048", name: "Alanya", aliases: ["alanya"] },
  { id: "A36079", name: "Formentera", aliases: ["formentera"] },
  { id: "D0839", name: "Dubai", aliases: ["dubai"] },
  { id: "M2005", name: "Mykonos", aliases: ["mykonos", "mikonos"] },
  { id: "M7512", name: "Mallorca", aliases: ["mallorca", "majorca", "majorque"] },
  { id: "İ3719", name: "Ibiza", aliases: ["ibiza", "eivissa"] },
];

/** Site categories ("kategori") with the ids used by the site's own search. */
export const CATEGORIES: {
  id: string;
  en: string;
  de: string;
  tr: string;
  /** Other spellings of the same Villamine category seen on pages (the site's own German/Turkish labels leak into English pages). */
  aliases?: string[];
  attribute: BooleanAttribute | null;
}[] = [
  { id: "4", en: "Sea View", de: "Meerblick", tr: "Deniz Manzaralı", attribute: "sea_view" },
  // Villamine business definition: "With Privacy" / Sichtschutz / Korunaklı / Muhafazakar = the pool /
  // private area is protected from outside view (docs/11-BUSINESS-RULES.md, Rule 4).
  { id: "5", en: "With Privacy", de: "Sichtschutz", tr: "Korunaklı", aliases: ["mit privatsphäre", "muhafazakar", "conservative villa"], attribute: "sight_protected" },
  { id: "6", en: "Near the Sea", de: "Nähe Meer", tr: "Denize Yakın", aliases: ["in meeresnähe"], attribute: "near_sea" },
  { id: "7", en: "In the Nature", de: "In der Natur", tr: "Doğa İçinde", attribute: "in_nature" },
  { id: "9", en: "Luxury", de: "Luxus", tr: "Lüks", attribute: "luxury" },
  { id: "13", en: "Heatable Pool", de: "Beheizbarer Pool", tr: "Isıtmalı Havuz", aliases: ["isıtma havuzlu"], attribute: "heated_pool" },
  // The site's own German label for id 16 is "Kinderpool"; it is NOT mapped (ambiguous: kids' pool vs child friendly).
  { id: "16", en: "Child Friendly", de: "Kinderfreundlich", tr: "Çocuk Dostu", attribute: "child_friendly" },
  { id: "3", en: "Honeymoon", de: "Flitterwochen", tr: "Balayı", attribute: "honeymoon" },
  { id: "78", en: "2026 Prices", de: "2026 Preise", tr: "2026 Fiyatlar", aliases: ["preise 2026", "2026 fiyatları"], attribute: null },
  { id: "17", en: "2027 Prices", de: "2027 Preise", tr: "2027 Fiyatlar", aliases: ["preise 2027", "2027 fiyatları"], attribute: null },
];

/**
 * Feature strings (hOZellikler) observed on villamine.com villa pages, mapped to
 * canonical attributes. Matching is exact on the normalised string (lower-case,
 * diacritics stripped). Strings not listed here are recorded as unmapped by the
 * sync so Villamine can review them; they never produce a TRUE value.
 *
 * The English page is the indexing locale; German/Turkish labels are included
 * for completeness only.
 */
export const FEATURE_MAP: Record<string, BooleanAttribute> = {
  // sea view
  "with a sea view": "sea_view",
  "sea view": "sea_view",
  meerblick: "sea_view",
  "deniz manzarali": "sea_view",
  // pools
  "private pool": "private_pool",
  "privater pool": "private_pool",
  "ozel havuz": "private_pool",
  "indoor pool heating": "indoor_pool",
  "indoor pool": "indoor_pool",
  "kapali havuz": "indoor_pool",
  "heated pool": "heated_pool",
  "heatable pool": "heated_pool",
  "isitmali havuz": "heated_pool",
  "kiddie pool": "children_pool",
  "children's pool": "children_pool",
  kinderpool: "children_pool",
  "cocuk havuzu": "children_pool",
  // wellness
  jacuzzi: "jacuzzi",
  whirlpool: "jacuzzi",
  jakuzi: "jacuzzi",
  sauna: "sauna",
  "turkish bath": "hammam",
  hammam: "hammam",
  hamam: "hammam",
  "sport room": "gym",
  gym: "gym",
  "fitness room": "gym",
  "spor odasi": "gym",
  // setting
  "in nature": "in_nature",
  "close to the sea": "near_sea",
  luxury: "luxury",
  "luxury villa": "luxury",
  "villa with heating pool": "heated_pool",
  "suitable for honeymoon": "honeymoon",
  // Villamine's "Conservative Villa" feature string = sight-protected pool / private area (Rule 4).
  "conservative villa": "sight_protected",
  "muhafazakar villa": "sight_protected",
  "muhafazakar": "sight_protected",
  // comfort
  "air conditioning": "air_conditioning",
  klimaanlage: "air_conditioning",
  klima: "air_conditioning",
  wifi: "wifi",
  "wi-fi": "wifi",
  internet: "wifi",
  "w-lan": "wifi",
  barbecue: "barbecue",
  grill: "barbecue",
  barbeku: "barbecue",
  "car park": "car_park",
  parking: "car_park",
  parkplatz: "car_park",
  "in der natur": "in_nature",
  garden: "garden",
  garten: "garden",
  bahce: "garden",
  terrace: "terrace",
  terrasse: "terrace",
  teras: "terrace",
  "game room": "game_room",
};

/**
 * Feature strings that are "included in the price" items or artefacts of the
 * site's translation rather than searchable property attributes. Listed so the
 * sync does not report them as unmapped.
 */
export const IGNORED_FEATURES = new Set([
  "this",
  "full item",
  "electric",
  "water use",
  "cylinder-gas usage",
  "pool-garden use",
  "weekly cleaning-sheets-towels",
  "kitchen equipment",
  "iron",
  "freezer",
  "washing machine",
  "tv",
  "dishwasher",
  "microwave",
  "open kitchen",
  "marble",
  "duplex",
  "with master bathroom",
  "bath",
  "ping pong",
  "pinball",
  "close to city center",
  "suitable for large family",
  "suitable for winter months",
  "shower",
  "cinema",
  "fireplace",
  "rental apartment",
  "extra cleaning",
  // German equivalents of the price-inclusion items above (label leakage on English pages)
  "extra linen-towel",
  "vollstandiger artikel",
  "elektrisch",
  "wasserverbrauch",
  "verwendung von flaschengas",
  "pool-garten-nutzung",
  "wochentliche reinigung-blatter-handtucher",
  "kuchenausstattung",
  "fernseher",
]);

/**
 * Feature strings that do not state an attribute directly but imply it. They
 * produce TRUE with provenance "derived" so the UI and the reply can label
 * them as indirect. Never treated as a direct Villamine statement.
 */
export const DERIVED_FEATURE_MAP: Record<string, { to: BooleanAttribute; note: string }> = {
  "convenient distance to the beach": {
    to: "near_sea",
    note: "Villamine lists 'Convenient Distance to the Beach' without a distance or the 'Near the Sea' category",
  },
};

/** Documented derivations between attributes (from -> to). */
export const DERIVED_ATTRIBUTES: { from: BooleanAttribute; to: BooleanAttribute }[] = [
  { from: "indoor_pool", to: "heated_pool" },
];

export const SITE_LOCALES: SiteLocale[] = ["en-us", "de-de", "tr-tr"];

/** Distance rule used for "walking distance to centre" style requests (documented). */
export const WALKING_DISTANCE_KM = 1.5;

export function villaUrl(slug: string, locale: SiteLocale): string {
  return `${VILLAMINE_ORIGIN}/${locale}/${slug}`;
}

/** Lower-case, strip diacritics (incl. Turkish dotless i), collapse whitespace. */
export function normaliseText(s: string): string {
  return s
    .replace(/İ/g, "I")
    .replace(/ı/g, "i")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

export function findRegion(name: string): { id: string; name: string } | null {
  const n = normaliseText(name);
  for (const r of REGIONS) {
    if (r.aliases.includes(n) || normaliseText(r.name) === n) return { id: r.id, name: r.name };
  }
  return null;
}
